import { fabric } from "fabric";

/** Hover tooltip showing an annotation's label/confidence. Mixed into BboxCanvas.prototype. */
export default {
    createTooltip(text, x, y, color) {
        // Remove existing tooltip if any
        this.removeTooltip();

        // Determine text color based on background color brightness
        let textColor = 'white'; // Default to white
        if (color && color.startsWith('rgba')) {
            // Extract RGB values from rgba string
            const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (match) {
                const r = parseInt(match[1]);
                const g = parseInt(match[2]);
                const b = parseInt(match[3]);
                // Calculate brightness using luminance formula
                const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                // Use black text for light backgrounds, white for dark backgrounds
                textColor = brightness > 128 ? 'black' : 'white';
            }
        }

        // Create tooltip background with annotation color
        this.tooltip = new fabric.Rect({
            fill: color,
            stroke: 'rgba(255, 255, 255, 0.3)',
            selectable: false,
            evented: false,
            hoverCursor: 'default'
        });

        // Create tooltip text with appropriate color
        this.tooltipText = new fabric.Text(text, {
            fill: textColor,
            fontFamily: 'Arial, sans-serif',
            selectable: false,
            evented: false,
            hoverCursor: 'default'
        });

        // Store tooltip references in canvas for access from other contexts
        this.canvas.tooltip = this.tooltip;
        this.canvas.tooltipText = this.tooltipText;

        // Add tooltip to canvas and bring to front
        this.canvas.add(this.tooltip);
        this.canvas.add(this.tooltipText);
        this.canvas.bringToFront(this.tooltip);
        this.canvas.bringToFront(this.tooltipText);

        this.positionTooltip(x, y);
    },

    // Sizes/positions the tooltip so its bottom-center sits at canvas point
    // (x, y) — i.e. right at the mouse pointer — and shrinks it as the
    // canvas zooms in so it keeps a constant on-screen size.
    positionTooltip(x, y) {
        if (!this.tooltip || !this.tooltipText) {
            return;
        }

        const zoom = this.canvas.getZoom();
        const fontSize = Math.max(8, 12 / zoom);
        const paddingX = 10 / zoom;
        const paddingY = 5 / zoom;

        this.tooltipText.set({ fontSize });

        const textWidth = this.tooltipText.width + paddingX * 2;
        const textHeight = this.tooltipText.height + paddingY * 2;

        let tooltipX = x - textWidth / 2;
        let tooltipY = y - textHeight;

        // Keep the tooltip on-screen (in canvas coordinate space).
        if (tooltipX < 0) {
            tooltipX = 0;
        } else if (tooltipX + textWidth > this.canvasWidth) {
            tooltipX = this.canvasWidth - textWidth;
        }
        if (tooltipY < 0) {
            // No room above the cursor — flip to below it instead.
            tooltipY = y;
        }

        this.tooltip.set({
            left: tooltipX,
            top: tooltipY,
            width: textWidth,
            height: textHeight,
            rx: 4 / zoom,
            ry: 4 / zoom,
            strokeWidth: 1 / zoom,
        });
        this.tooltipText.set({
            left: tooltipX + paddingX,
            top: tooltipY + paddingY,
        });

        this.canvas.requestRenderAll();
    },

    removeTooltip() {
        if (this.tooltip) {
            this.canvas.remove(this.tooltip);
            this.tooltip = null;
        }
        if (this.tooltipText) {
            this.canvas.remove(this.tooltipText);
            this.tooltipText = null;
        }
        // Also clear canvas references
        if (this.canvas.tooltip) {
            this.canvas.remove(this.canvas.tooltip);
            this.canvas.tooltip = null;
        }
        if (this.canvas.tooltipText) {
            this.canvas.remove(this.canvas.tooltipText);
            this.canvas.tooltipText = null;
        }
        this.canvas.requestRenderAll();
    },
};
